import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getPortfolioStoryBySlug, portfolioStories } from "./portfolioData";
import "./portfolio.css";

const defaultSections = [
  { id: "overview", label: "Overview" },
  { id: "services", label: "Services" },
  { id: "how-we-did-it", label: "How we did it" },
  { id: "impact", label: "Impact" },
];

function MotionGallery({ images, title }) {
  const scrollRef = useRef(null);
  const isHoveredRef = useRef(false);
  const animationFrameRef = useRef(null);
  const safeImages = useMemo(() => images || [], [images]);
  const loopImages = useMemo(() => [...safeImages, ...safeImages], [safeImages]);
  const dragStateRef = useRef({
    isPointerDown: false,
    startX: 0,
    startScrollLeft: 0,
  });

  useEffect(() => {
    const element = scrollRef.current;
    if (!element || !safeImages.length) {
      return undefined;
    }

    const normalizeScrollPosition = () => {
      const loopWidth = element.scrollWidth / 2;
      if (!loopWidth) {
        return;
      }

      if (element.scrollLeft >= loopWidth) {
        element.scrollLeft -= loopWidth;
      } else if (element.scrollLeft < 0) {
        element.scrollLeft += loopWidth;
      }
    };

    element.scrollLeft = 0;

    const step = () => {
      if (!isHoveredRef.current && !dragStateRef.current.isPointerDown) {
        const loopWidth = element.scrollWidth / 2;
        if (loopWidth > 0) {
          element.scrollLeft += 0.45;
          normalizeScrollPosition();
        }
      }

      animationFrameRef.current = window.requestAnimationFrame(step);
    };

    animationFrameRef.current = window.requestAnimationFrame(step);

    return () => {
      if (animationFrameRef.current) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [safeImages]);

  const handlePointerDown = (event) => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }

    dragStateRef.current = {
      isPointerDown: true,
      startX: event.clientX,
      startScrollLeft: element.scrollLeft,
    };

    element.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event) => {
    const element = scrollRef.current;
    const dragState = dragStateRef.current;
    if (!element || !dragState.isPointerDown) {
      return;
    }

    const deltaX = event.clientX - dragState.startX;
    element.scrollLeft = dragState.startScrollLeft - deltaX;
  };

  const handlePointerUp = (event) => {
    const element = scrollRef.current;
    dragStateRef.current.isPointerDown = false;

    if (element && element.hasPointerCapture(event.pointerId)) {
      element.releasePointerCapture(event.pointerId);
    }
  };

  const handleWheel = (event) => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }

    if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
      element.scrollLeft += event.deltaY;
      event.preventDefault();
    }
  };

  return (
    <div className="portfolio-motion-gallery">
      <div className="portfolio-motion-gallery__label">{title}</div>
      <div
        ref={scrollRef}
        className="portfolio-motion-gallery__viewport"
        onMouseEnter={() => {
          isHoveredRef.current = true;
        }}
        onMouseLeave={() => {
          isHoveredRef.current = false;
          dragStateRef.current.isPointerDown = false;
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onWheel={handleWheel}
      >
        <div className="portfolio-motion-gallery__track">
          {loopImages.map((image, index) => (
            <div key={`${image}-${index}`} className="portfolio-motion-gallery__item">
              <img
                src={image}
                alt={`${title} panel ${(index % safeImages.length) + 1}`}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DetailContentBlock({ block }) {
  if (block.type === "text") {
    return (
      <div className="portfolio-text-block">
        <h3>{block.title}</h3>
        <p>{block.body}</p>
      </div>
    );
  }

  if (block.type === "imageText") {
    return (
      <div className={`portfolio-step portfolio-step--${block.imagePosition}`}>
        <div className="portfolio-step__copy">
          <h3>{block.title}</h3>
          <p>{block.body}</p>
        </div>
        <div className="portfolio-step__media">
          <img src={block.image} alt={block.title} />
        </div>
      </div>
    );
  }

  if (block.type === "fullImage") {
    return (
      <div className="portfolio-supporting-image">
        <img src={block.image} alt={block.alt} />
      </div>
    );
  }

  if (block.type === "gallery") {
    return (
      <div className="portfolio-image-row">
        {(block.images || []).map((image, index) => (
          <div key={`${image}-${index}`} className="portfolio-image-card">
            <img src={image} alt={`${block.altPrefix} ${index + 1}`} />
          </div>
        ))}
      </div>
    );
  }

  if (block.type === "quote") {
    return (
      <blockquote className="portfolio-quote-block">
        <p>{block.body}</p>
      </blockquote>
    );
  }

  return null;
}

function PortfolioPlaceholder({ story }) {
  const moreStories = portfolioStories
    .filter((item) => item.slug !== story.slug)
    .slice(0, 3);

  return (
    <div className="portfolio-detail-page">
      <section className="portfolio-detail-hero">
        <div className="portfolio-detail-hero__content">
          <div className="portfolio-detail-hero__eyebrow-row">
            <p className="portfolio-detail-kicker">Portfolio Details</p>
          </div>
          <div className="portfolio-detail-hero__split">
            <div className="portfolio-detail-hero__title-block">
              <h1>{story.heroTitle || story.title}</h1>
            </div>
            <div className="portfolio-detail-hero__description-block">
              <p>{story.heroDescription || story.description}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="portfolio-placeholder">
        <div className="portfolio-placeholder__card">
          <h2>Details page in progress</h2>
          <p>
            This story now has a working route, and the full case-study content
            can be dropped in here when it is ready.
          </p>
          <Link to="/portfolio" className="portfolio-detail-backlink">
            Back to all stories
          </Link>
        </div>
      </section>

      <section className="portfolio-more-stories">
        <div className="portfolio-section-heading">
          <p>More Stories</p>
          <h2>Explore other work</h2>
        </div>
        <div className="portfolio-more-stories__grid">
          {moreStories.map((item) => (
            <Link
              key={item.slug}
              to={`/portfolio/${item.slug}`}
              className="portfolio-story-mini"
            >
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

function PortfolioDetailPage() {
  const { slug } = useParams();
  const story = getPortfolioStoryBySlug(slug);
  const [activeSection, setActiveSection] = useState("overview");

  const moreStories = useMemo(
    () =>
      portfolioStories
        .filter((item) => item.slug !== slug)
        .sort(() => Math.random() - 0.5)
        .slice(0, 3),
    [slug]
  );

  useEffect(() => {
    if (!story?.detail) {
      return undefined;
    }

    const ids = (story.detail.sectionNav || defaultSections).map(
      (section) => section.id
    );
    const handleScroll = () => {
      const offset = 180;
      let currentSection = ids[0];

      ids.forEach((id) => {
        const element = document.getElementById(id);
        if (!element) {
          return;
        }

        const top = element.getBoundingClientRect().top;
        if (top - offset <= 0) {
          currentSection = id;
        }
      });

      setActiveSection(currentSection);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [story]);

  if (!story) {
    return (
      <div className="portfolio-placeholder portfolio-placeholder--missing">
        <div className="portfolio-placeholder__card">
          <h2>Portfolio story not found</h2>
          <Link to="/portfolio" className="portfolio-detail-backlink">
            Back to all stories
          </Link>
        </div>
      </div>
    );
  }

  if (story.status !== "published" || !story.detail) {
    return <PortfolioPlaceholder story={story} />;
  }

  const { detail } = story;
  const sectionNav = detail.sectionNav || defaultSections;

  return (
    <div className="portfolio-detail-page">
      <section className="portfolio-detail-hero">
        <div className="portfolio-detail-hero__content">
          <div className="portfolio-detail-hero__eyebrow-row">
            <p className="portfolio-detail-kicker">AI-Powered Personalization</p>
          </div>
          <div className="portfolio-detail-hero__split">
            <div className="portfolio-detail-hero__title-block">
              <h1>{story.heroTitle}</h1>
            </div>
            <div className="portfolio-detail-hero__description-block">
              <p>{story.heroDescription}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="portfolio-detail-cover">
        <img src={detail.coverImage} alt={story.heroTitle} />
      </section>

      <nav className="portfolio-detail-nav">
        <div className="portfolio-detail-nav__inner">
          {sectionNav.map((section) => (
            <a
              key={section.id}
              href={`#${section.id}`}
              className={
                activeSection === section.id
                  ? "portfolio-detail-nav__link active"
                  : "portfolio-detail-nav__link"
              }
            >
              {section.label}
            </a>
          ))}
        </div>
      </nav>

      <section id="overview" className="portfolio-detail-section">
        <div className="portfolio-overview-grid">
          <aside className="portfolio-services-panel">
            <p className="portfolio-services-panel__eyebrow">Services</p>
            <ul>
              {(detail.overviewServices || []).map((service) => (
                <li key={service.label}>
                  <Link to={service.path} className="portfolio-services-panel__link">
                    {service.label}
                  </Link>
                </li>
              ))}
            </ul>
          </aside>
          <div className="portfolio-copy-panel">
            <div className="portfolio-section-heading portfolio-section-heading--left">
              <p>Overview</p>
              <h2>{detail.overviewHeading || "Turning enterprise-scale retail data into better shopping experiences"}</h2>
            </div>
            {(detail.overview || []).map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </div>

        {detail.overviewGallery?.length ? (
          <div className="portfolio-image-row">
            {detail.overviewGallery.map((image, index) => (
              <div key={image} className="portfolio-image-card">
                <img src={image} alt={`${story.heroTitle} overview visual ${index + 1}`} />
              </div>
            ))}
          </div>
        ) : null}
      </section>

      <section id="services" className="portfolio-detail-section">
        <div className="portfolio-section-heading portfolio-section-heading--left">
          <p>Services</p>
          <h2>{detail.servicesHeading || "Building a recommendation and prediction engine for modern retail"}</h2>
        </div>
        <div className="portfolio-narrative">
          {(detail.servicesCopy || []).map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </div>

        {detail.servicesFeatureBlock ? (
          <div
            className={`portfolio-step portfolio-step--${detail.servicesFeatureBlock.imagePosition}`}
          >
            <div className="portfolio-step__copy">
              <h3>{detail.servicesFeatureBlock.title}</h3>
              <p>{detail.servicesFeatureBlock.body}</p>
            </div>
            <div className="portfolio-step__media">
              <img
                src={detail.servicesFeatureBlock.image}
                alt={detail.servicesFeatureBlock.title}
              />
            </div>
          </div>
        ) : null}

        {detail.coreHighlightsImages?.length ? (
          <MotionGallery
            images={detail.coreHighlightsImages}
            title={detail.coreHighlightsTitle}
          />
        ) : null}
      </section>

      <section id="how-we-did-it" className="portfolio-detail-section">
        <div className="portfolio-section-heading portfolio-section-heading--left">
          <p>How we did it</p>
          <h2>{detail.howWeDidItHeading || "From raw behavioral data to intelligent, real-time retail decisions"}</h2>
        </div>
        <p className="portfolio-section-intro">
          {detail.howWeDidItIntro ||
            "In only a few weeks, Zedflux delivered a data-preparation pipeline and machine-learning core that could power real-time upsell and cross-sell recommendations across the retailer's web and mobile channels."}
        </p>

        {detail.processSteps?.length ? (
          <div className="portfolio-steps">
            {(detail.processSteps || []).map((step) => (
              <div
                key={step.title}
                className={`portfolio-step portfolio-step--${step.imagePosition}`}
              >
                <div className="portfolio-step__copy">
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                </div>
                <div className="portfolio-step__media">
                  <img src={step.image} alt={step.title} />
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {detail.contentBlocks?.length ? (
          <div className="portfolio-content-blocks">
            {(detail.contentBlocks || []).map((block, index) => (
              <DetailContentBlock
                key={`${block.type}-${block.title || block.image || index}`}
                block={block}
              />
            ))}
          </div>
        ) : null}

        {detail.supportingImage ? (
          <div className="portfolio-supporting-image">
            <img src={detail.supportingImage} alt={`${story.heroTitle} supporting visual`} />
          </div>
        ) : null}
      </section>

      <section id="impact" className="portfolio-detail-section">
        <div className="portfolio-impact-grid">
          <div className="portfolio-impact-copy">
            <div className="portfolio-section-heading portfolio-section-heading--left">
              <p>Impact</p>
              <h2>{detail.impactHeading || "Fast execution, stronger engagement, measurable growth"}</h2>
            </div>
            {(detail.impactCopy || []).map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}

            <div className="portfolio-stats-grid">
              {(detail.impactStats || []).map((stat) => (
                <div key={stat.label} className="portfolio-stat-card">
                  <span className="portfolio-stat-card__value">{stat.value}</span>
                  <span className="portfolio-stat-card__label">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="portfolio-impact-media">
            <img src={detail.impactImage} alt={`${story.heroTitle} impact visual`} />
          </div>
        </div>
      </section>

      <section className="portfolio-more-stories">
        <div className="portfolio-section-heading">
          <p>More Stories</p>
          <h2>Explore what else we&apos;ve been building</h2>
        </div>
        <div className="portfolio-more-stories__grid">
          {moreStories.map((item) => (
            <Link
              key={item.slug}
              to={`/portfolio/${item.slug}`}
              className="portfolio-story-mini"
            >
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

export default PortfolioDetailPage;
